import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Fish, Warning, FilePdf, Trash, PencilSimple, Copy, Plus, Eye, FileText,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { ConfirmDialog } from "../components/ConfirmDialog";
import RichEditor from "../components/RichEditor";
import { useAuth } from "../lib/auth";

const LANDING_MODES = [
  { id: "training", label: "Page pédagogique (recommandé — la cible apprend qu'elle a été piégée)" },
  { id: "redirect", label: "Redirection silencieuse (vers un site légitime — la cible ne sait rien)" },
];

export default function Phishing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [targets, setTargets] = useState("");
  const [company, setCompany] = useState("");
  const [landingMode, setLandingMode] = useState("training");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmLaunch, setConfirmLaunch] = useState(null);
  const [confirmDeleteTpl, setConfirmDeleteTpl] = useState(null);

  // Template editor state
  const [editor, setEditor] = useState(null); // { mode:'create'|'edit', id?, name, subject, html, source? }
  const [preview, setPreview] = useState(null); // { name, subject, html }
  const [savingTpl, setSavingTpl] = useState(false);

  const loadCampaigns = () =>
    api.get("/modules/phishing-sim/campaigns").then((r) => setCampaigns(r.data.campaigns || [])).catch(() => {});

  const loadTemplates = () =>
    api.get("/modules/phishing-sim/templates").then((r) => {
      setTemplates(r.data.templates || []);
      if (r.data.templates?.[0]) setTemplateId((prev) => prev || r.data.templates[0].id);
    }).catch(() => {});

  useEffect(() => { loadTemplates(); loadCampaigns(); }, []);

  const parseEmails = () =>
    targets.split(/[\s,;\n]+/).map((e) => e.trim().toLowerCase()).filter((e) => e && e.includes("@"));

  const openLaunchConfirm = () => {
    const emails = parseEmails();
    if (!name || !templateId || emails.length === 0) { toast.error("Nom + template + au moins 1 email requis"); return; }
    if (landingMode === "redirect" && !/^https?:\/\/[^\s]+$/i.test(redirectUrl)) {
      toast.error("URL de redirection invalide (http:// ou https:// requis)");
      return;
    }
    setConfirmLaunch({ emails, count: emails.length });
  };

  const launch = async () => {
    const emails = parseEmails();
    setConfirmLaunch(null);
    setBusy(true);
    try {
      const r = await api.post("/modules/phishing-sim/campaigns", {
        name, template_id: templateId, target_emails: emails,
        client_company: company || null,
        landing_mode: landingMode,
        redirect_url: landingMode === "redirect" ? redirectUrl.trim() : null,
      });
      const sent = r.data?.sent ?? emails.length;
      const failed = r.data?.failed ?? 0;
      toast.success(`Campagne lancée · ${sent} email(s) envoyé(s)${failed ? ` · ${failed} échec(s)` : ""}`);
      setName(""); setTargets(""); setCompany(""); setRedirectUrl("");
      loadCampaigns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  // -------- Template CRUD --------
  const openCreate = () =>
    setEditor({
      mode: "create", id: null, source: "custom",
      name: "", subject: "",
      html: '<!--nosig--><div style="font-family:Arial,sans-serif;max-width:600px;padding:20px">\n  <h2>Bonjour,</h2>\n  <p>Rédigez ici le contenu de votre email piégé.</p>\n  <p><a href="{TRACK_URL}" style="display:inline-block;background:#2563eb;color:#fff;padding:12px 24px;text-decoration:none;border-radius:6px;font-weight:600">Cliquez ici</a></p>\n</div>',
    });

  const openEdit = async (t) => {
    try {
      const r = await api.get(`/modules/phishing-sim/templates/${t.id}`);
      setEditor({ mode: "edit", id: t.id, source: r.data.source, name: r.data.name, subject: r.data.subject, html: r.data.html });
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur chargement template"); }
  };

  const duplicate = async (t) => {
    try {
      const r = await api.post(`/modules/phishing-sim/templates/${t.id}/duplicate`);
      toast.success(`Copie créée : ${r.data.name}`);
      await loadTemplates();
      setEditor({ mode: "edit", id: r.data.id, source: "custom", name: r.data.name, subject: r.data.subject, html: r.data.html });
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const saveTemplate = async () => {
    if (!editor) return;
    if (!editor.name || editor.name.length < 3) { toast.error("Nom du template requis (min 3 caractères)"); return; }
    if (!editor.subject || editor.subject.length < 3) { toast.error("Objet requis (min 3 caractères)"); return; }
    if (!editor.html.includes("{TRACK_URL}")) {
      toast.error("Le HTML doit contenir {TRACK_URL} — placez ce placeholder dans le lien piégé");
      return;
    }
    setSavingTpl(true);
    try {
      if (editor.mode === "create") {
        const r = await api.post("/modules/phishing-sim/templates", { name: editor.name, subject: editor.subject, html: editor.html });
        toast.success("Template créé");
        setTemplateId(r.data.id);
      } else {
        await api.put(`/modules/phishing-sim/templates/${editor.id}`, { name: editor.name, subject: editor.subject, html: editor.html });
        toast.success("Template mis à jour");
      }
      setEditor(null);
      loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSavingTpl(false); }
  };

  const doDeleteTpl = async () => {
    if (!confirmDeleteTpl) return;
    const id = confirmDeleteTpl.id;
    setConfirmDeleteTpl(null);
    try {
      await api.delete(`/modules/phishing-sim/templates/${id}`);
      toast.success("Template supprimé");
      loadTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const openPreview = async (t) => {
    try {
      const r = await api.get(`/modules/phishing-sim/templates/${t.id}`);
      setPreview({ name: r.data.name, subject: r.data.subject, html: r.data.html.replace(/{TRACK_URL}/g, "#") });
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  // -------- Campaign helpers --------
  const downloadPdf = async (c) => {
    try {
      const r = await api.get(`/modules/phishing-sim/campaigns/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `phishing-${c.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${c.id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Erreur téléchargement PDF: " + (e?.response?.data?.detail || e.message));
    }
  };

  const isAdmin = user?.role === "admin";
  const deleteCampaign = async () => {
    if (!confirmDelete) return;
    const id = confirmDelete.id;
    setConfirmDelete(null);
    try {
      await api.delete(`/modules/phishing-sim/campaigns/${id}`);
      toast.success("Campagne supprimée");
      setCampaigns((prev) => prev.filter((x) => x.id !== id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="p-6 max-w-5xl">
      <button onClick={() => nav("/modules")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="phishing-back">
        <ArrowLeft size={16}/> Modules
      </button>
      <h1 className="text-3xl font-display font-bold mb-1 flex items-center gap-2"><Fish size={28}/> Simulateur de Phishing</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Les cibles reçoivent un email de phishing factice envoyé <b>depuis votre boîte MS Graph</b>. La page de destination affiche un message éducatif (ou redirige silencieusement). Aucune credential n'est capturée.
      </p>

      <div className="rounded-xl border bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/60 p-4 mb-6 flex gap-3">
        <Warning size={20} className="text-amber-700 shrink-0"/>
        <div className="text-xs text-amber-900 dark:text-amber-100">
          <b>Autorisation requise</b> : une simulation sans accord écrit du client peut être qualifiée d'intrusion (art. 323-1 CP). Récupérez toujours une autorisation par email avant lancement, et conservez-la avec le rapport PDF.
        </div>
      </div>

      {/* -------- Template management -------- */}
      <div className="rounded-xl border bg-card p-5 mb-6" data-testid="phi-templates-panel">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div>
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><FileText size={18}/> Templates</h2>
            <p className="text-xs text-muted-foreground">Les 7 modèles intégrés sont en lecture seule. Dupliquez-en un ou créez le vôtre pour tout personnaliser (logos, textes, liens…).</p>
          </div>
          <Button variant="outline" size="sm" onClick={openCreate} data-testid="phi-tpl-new">
            <Plus size={14} className="mr-1"/> Nouveau template
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {templates.map((t) => (
            <div key={t.id} className="border rounded-lg p-3 flex items-start justify-between gap-3 hover:bg-muted/30" data-testid={`phi-tpl-${t.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium truncate">{t.name}</span>
                  <span className={`text-[0.6rem] px-1.5 py-0.5 rounded-full ${t.source === "builtin" ? "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300"}`}>
                    {t.source === "builtin" ? "Intégré" : "Personnalisé"}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">{t.subject}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => openPreview(t)} data-testid={`phi-tpl-preview-${t.id}`} title="Aperçu">
                  <Eye size={14}/>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => duplicate(t)} data-testid={`phi-tpl-dup-${t.id}`} title="Dupliquer">
                  <Copy size={14}/>
                </Button>
                {t.source === "custom" && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`phi-tpl-edit-${t.id}`} title="Modifier">
                      <PencilSimple size={14}/>
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteTpl(t)} data-testid={`phi-tpl-del-${t.id}`} title="Supprimer">
                      <Trash size={14} className="text-red-600"/>
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* -------- Campaign form -------- */}
      <div className="rounded-xl border bg-card p-5 space-y-4 mb-6">
        <h2 className="font-display font-semibold text-lg">Nouvelle campagne</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Nom de la campagne</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Sensibilisation trimestrielle Q1" data-testid="phi-name" disabled={busy}/>
          </div>
          <div>
            <Label>Client concerné (optionnel)</Label>
            <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Acme Corp" disabled={busy}/>
          </div>
          <div className="md:col-span-2">
            <Label>Modèle d'email piégé</Label>
            <Select value={templateId} onValueChange={setTemplateId} disabled={busy}>
              <SelectTrigger data-testid="phi-template"><SelectValue placeholder="Choisir un modèle"/></SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} <span className="text-xs text-muted-foreground">— {t.subject}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Page de destination après clic</Label>
            <Select value={landingMode} onValueChange={setLandingMode} disabled={busy}>
              <SelectTrigger data-testid="phi-landing-mode"><SelectValue/></SelectTrigger>
              <SelectContent>
                {LANDING_MODES.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {landingMode === "redirect" && (
            <div className="md:col-span-2">
              <Label>URL de redirection (site légitime)</Label>
              <Input value={redirectUrl} onChange={(e) => setRedirectUrl(e.target.value)} placeholder="https://intranet.acme.fr/securite" data-testid="phi-redirect-url" disabled={busy}/>
            </div>
          )}
          <div className="md:col-span-2">
            <Label>Destinataires (un email par ligne, ou séparés par virgule)</Label>
            <Textarea value={targets} onChange={(e) => setTargets(e.target.value)} rows={5} placeholder={"alice@client.fr\nbob@client.fr"} data-testid="phi-targets" disabled={busy}/>
            <div className="text-xs text-muted-foreground mt-1">{parseEmails().length} email(s) valide(s) détecté(s)</div>
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={openLaunchConfirm} disabled={busy} size="lg" data-testid="phi-launch">
            {busy ? "Envoi..." : "🎣 Lancer la campagne"}
          </Button>
        </div>
      </div>

      {/* -------- Campaign list -------- */}
      <h2 className="font-display font-semibold text-xl mb-3">Campagnes récentes</h2>
      {campaigns.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed p-8 text-center text-muted-foreground" data-testid="phi-empty">
          Aucune campagne lancée pour l'instant.
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <div key={c.id} className="rounded-xl border bg-card p-4" data-testid={`phi-campaign-${c.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {c.client_company || "—"} · {new Date(c.created_at).toLocaleString("fr-FR")} · par {c.author_name}
                    {" · "}<span className="italic">{c.landing_mode === "redirect" ? "redirection" : "page pédagogique"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${c.click_rate > 30 ? "text-red-600" : c.click_rate > 10 ? "text-amber-600" : "text-emerald-600"}`}>{c.click_rate}%</div>
                    <div className="text-xs text-muted-foreground">{c.clicked} clic(s) / {c.sent} envoyé(s){c.failed ? ` · ${c.failed} échec(s)` : ""}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => downloadPdf(c)} data-testid={`phi-pdf-${c.id}`} title="Rapport PDF">
                      <FilePdf size={16}/>
                    </Button>
                    {isAdmin && (
                      <Button variant="outline" size="sm" onClick={() => setConfirmDelete(c)} data-testid={`phi-delete-${c.id}`} title="Supprimer">
                        <Trash size={16}/>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* -------- Template editor modal -------- */}
      <Dialog open={!!editor} onOpenChange={(v) => !v && setEditor(null)}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editor?.mode === "create" ? "Nouveau template de phishing" : `Modifier « ${editor?.name} »`}</DialogTitle>
            <DialogDescription>
              Rédigez le contenu de l'email piégé. Placez le lien piégé sur le placeholder <span className="mono">{"{TRACK_URL}"}</span> — c'est ce qui sera remplacé par l'URL de tracking unique par cible.
            </DialogDescription>
          </DialogHeader>
          {editor && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Nom du template</Label>
                  <Input
                    value={editor.name}
                    onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                    placeholder="Ex: Alerte facture URSSAF"
                    data-testid="phi-tpl-editor-name"
                  />
                </div>
                <div>
                  <Label>Objet de l'email</Label>
                  <Input
                    value={editor.subject}
                    onChange={(e) => setEditor({ ...editor, subject: e.target.value })}
                    placeholder="Ex: Votre facture 2026-11 est disponible"
                    data-testid="phi-tpl-editor-subject"
                  />
                </div>
              </div>
              <div>
                <Label>Contenu HTML</Label>
                <div className="text-xs text-muted-foreground mb-2">
                  Utilisez la barre d'outils pour formater, insérer un logo (bouton image) ou coller directement du HTML. Le placeholder <span className="mono">{"{TRACK_URL}"}</span> doit apparaître au moins une fois — placez-le dans le <span className="mono">href</span> de votre bouton principal.
                </div>
                <div className="border rounded-lg">
                  <RichEditor
                    key={editor.id || "new"}
                    value={editor.html}
                    onChange={(html) => setEditor((prev) => (prev ? { ...prev, html } : prev))}
                    minHeight={340}
                  />
                </div>
                {!editor.html.includes("{TRACK_URL}") && (
                  <div className="text-xs text-red-600 mt-2 flex items-center gap-1">
                    <Warning size={12}/> {"{TRACK_URL}"} manque — la campagne ne pourra pas être créée
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)} disabled={savingTpl}>Annuler</Button>
            <Button onClick={saveTemplate} disabled={savingTpl} data-testid="phi-tpl-save">
              {savingTpl ? "Enregistrement…" : editor?.mode === "create" ? "Créer le template" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------- Template preview modal -------- */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu — {preview?.name}</DialogTitle>
            <DialogDescription>Objet : {preview?.subject}</DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="border rounded-lg p-4 bg-slate-100 dark:bg-slate-900" data-testid="phi-tpl-preview-box">
              <div className="bg-white dark:bg-white text-black rounded shadow-sm p-2" dangerouslySetInnerHTML={{ __html: preview.html }}/>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmLaunch}
        onOpenChange={(v) => !v && setConfirmLaunch(null)}
        title="Lancer la campagne de phishing ?"
        description={confirmLaunch ? `Un email piégé va être envoyé à ${confirmLaunch.count} destinataire(s) depuis votre boîte Microsoft 365. Vérifiez d'avoir l'autorisation écrite du client — vous êtes seul responsable de cette simulation.` : ""}
        confirmText="Lancer"
        onConfirm={launch}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Supprimer cette campagne ?"
        description={confirmDelete ? `« ${confirmDelete.name} » sera définitivement supprimée avec tous ses résultats (${confirmDelete.clicked} clic(s) enregistré(s)). Action irréversible.` : ""}
        confirmText="Supprimer"
        variant="destructive"
        onConfirm={deleteCampaign}
      />
      <ConfirmDialog
        open={!!confirmDeleteTpl}
        onOpenChange={(v) => !v && setConfirmDeleteTpl(null)}
        title="Supprimer ce template ?"
        description={confirmDeleteTpl ? `« ${confirmDeleteTpl.name} » sera définitivement supprimé. Les campagnes déjà lancées avec ce template ne seront pas affectées.` : ""}
        confirmText="Supprimer"
        variant="destructive"
        onConfirm={doDeleteTpl}
      />
    </div>
  );
}
